import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { MEDICAL_DEPLOYMENT_LABELS } from "constants/dashboardTerminology";
import { getEstadoOperativoVisual } from "utils/operationalStatus";

const DEFAULT_ALLOWED_ROLES = ["1", "2", "6", "8"];

const initialFilters = {
  estado: "",
  asistencia: "",
  q: "",
};

const emptyResponse = {
  convocatoria: {},
  summary: {},
  items: [],
  appliedFilters: {},
};

const estadoOptions = [
  { value: "", label: "Todos" },
  { value: "1", label: "1 · Aceptado" },
  { value: "2", label: "2 · Rechazado" },
  { value: "3", label: "3 · En espera" },
  { value: "4", label: "4 · Cancelado" },
];

export const asistenciaOptions = [
  { value: "", label: "Todas" },
  { value: "5", label: "Asistente" },
  { value: "6", label: "Retrasado" },
  { value: "7", label: "Inasistente" },
  { value: "8", label: "Suplente" },
];

const asistenciaLabels = asistenciaOptions.reduce((labels, option) => {
  if (option.value) labels[option.value] = option.label;
  return labels;
}, {});

const buildParams = (filters) => {
  const params = {};

  if (filters.estado !== "") params.estado = filters.estado;
  if (filters.asistencia !== "") params.asistencia = filters.asistencia;
  if (filters.q.trim()) params.q = filters.q.trim();

  return params;
};

const getStoredRole = () => {
  try {
    return String(JSON.parse(localStorage.getItem("token") || "{}")?.rol || "");
  } catch (error) {
    return "";
  }
};

const formatDate = (value) => {
  if (!value || typeof value !== "string" || !value.includes("-")) return value || "-";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
};

export const formatActivity = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "-";

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).join(", ") || "-";
    } catch (error) {
      return trimmed;
    }

    return trimmed;
  }

  return value ?? "-";
};

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const getEstadoParticipacionVisual = (label) => {
  const normalized = normalizeText(label);

  if (normalized === "aceptado") return { chipColor: "success", border: "#22a06b" };
  if (normalized === "en espera") return { chipColor: "warning", border: "#eab308" };
  if (normalized === "rechazado") return { chipColor: "error", border: "#d94841" };
  if (normalized === "cancelado") return { chipColor: "default", border: "#94a3b8" };
  return { chipColor: "default", border: "#cbd5e1" };
};

const getAsistenciaColor = (label) => {
  const normalized = normalizeText(label);
  if (normalized === "asistente") return "success";
  if (["retrasado", "retraso", "tardanza"].includes(normalized)) return "warning";
  if (normalized === "inasistente") return "error";
  if (normalized === "suplente") return "info";
  return "default";
};

const getRequestErrorMessage = (requestError) => {
  const status = requestError?.response?.status;
  const backendMessage = requestError?.response?.data?.message || requestError?.response?.data?.error;

  if (status === 400) return "Los filtros enviados no son válidos.";
  if (status === 401) return backendMessage || "No fue posible autenticar la solicitud.";
  if (status === 403) return "No tiene permiso para consultar los voluntarios de este despliegue.";
  if (status === 404) return "Convocatoria no encontrada.";
  return "No se pudo consultar el listado de voluntarios.";
};

const SummaryItem = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      height: "100%",
      bgcolor: "background.default",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 900 }}>
      {Number(value) || 0}
    </Typography>
  </Paper>
);

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const VoluntariosConvocatoriaOperativaDialog = ({
  open,
  onClose,
  convocatoria,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
}) => {
  const requestRef = useRef(null);
  const requestIdRef = useRef(0);
  const [contextId, setContextId] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [responseData, setResponseData] = useState(emptyResponse);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const idConvocatoria =
    convocatoria?.idConvocatoria ?? convocatoria?.id_convocatoria ?? convocatoria?.id;
  const idIsValid = Number.isFinite(Number(idConvocatoria)) && Number(idConvocatoria) > 0;
  const currentRole = getStoredRole();
  const canView = allowedRoles.map(String).includes(currentRole);
  const currentContextId = idIsValid ? String(idConvocatoria) : "";
  const contextIsReady = contextId === currentContextId;

  useEffect(() => {
    if (!open || contextId === currentContextId) return;

    if (requestRef.current) {
      requestRef.current.controller.abort();
      requestRef.current = null;
    }
    requestIdRef.current += 1;

    setContextId(currentContextId);
    setFilters({ ...initialFilters });
    setAppliedFilters({ ...initialFilters });
    setResponseData({ ...emptyResponse });
    setError("");
    setLoading(false);
    setPage(0);
  }, [contextId, currentContextId, open]);

  const loadData = useCallback(async () => {
    if (!open || !canView || !idIsValid || !contextIsReady) return;

    const params = buildParams(appliedFilters);
    const requestKey = `${idConvocatoria}:${JSON.stringify(params)}`;

    if (requestRef.current?.key === requestKey) return;

    if (requestRef.current) {
      requestRef.current.controller.abort();
    }

    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    requestRef.current = { controller, key: requestKey, requestId };
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `/dashboard-operativo/convocatorias/${idConvocatoria}/voluntarios`,
        { params, signal: controller.signal }
      );

      if (requestId !== requestIdRef.current) return;

      const payload = response?.data?.data || response?.data || {};
      setResponseData({
        convocatoria: payload.convocatoria || {},
        summary: payload.summary || {},
        items: Array.isArray(payload.items) ? payload.items : [],
        appliedFilters: payload.appliedFilters || {},
      });
    } catch (requestError) {
      if (axios.isCancel(requestError) || requestError?.code === "ERR_CANCELED") return;
      if (requestId !== requestIdRef.current) return;
      setError(getRequestErrorMessage(requestError));
    } finally {
      if (requestId === requestIdRef.current) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }, [appliedFilters, canView, contextIsReady, idConvocatoria, idIsValid, open]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!open && requestRef.current) {
      requestRef.current.controller.abort();
      requestRef.current = null;
      requestIdRef.current += 1;
      setLoading(false);
    }
  }, [open]);

  const hasAppliedFilters = Boolean(
    appliedFilters.estado !== "" || appliedFilters.asistencia !== "" || appliedFilters.q.trim()
  );
  const headerData = Object.keys(responseData.convocatoria).length
    ? responseData.convocatoria
    : convocatoria || {};
  const estadoOperativoVisual = getEstadoOperativoVisual(headerData.estadoOperativo);
  const items = responseData.items;
  const paginatedItems = useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  );
  const fromRow = items.length === 0 ? 0 : page * rowsPerPage + 1;
  const toRow = Math.min((page + 1) * rowsPerPage, items.length);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(items.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [items.length, page, rowsPerPage]);

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters({ ...initialFilters });
    setAppliedFilters({ ...initialFilters });
    setPage(0);
  };

  const summaryItems = [
    ["Total", responseData.summary.total],
    ["Aceptados", responseData.summary.aceptados],
    ["En espera", responseData.summary.enEspera],
    ["Rechazados", responseData.summary.rechazados],
    ["Cancelados", responseData.summary.cancelados],
    ["Asistencia procesada", responseData.summary.asistenciaProcesada],
    ["Pendientes de asistencia", responseData.summary.pendientesAsistencia],
    ["Con refrigerio", responseData.summary.conRefrigerio],
  ];

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { maxHeight: "94vh" } }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 900 }}>
          Voluntarios del despliegue
        </Typography>
        <Tooltip title="Cerrar">
          <span>
            <IconButton
              onClick={onClose}
              disabled={loading}
              sx={{ position: "absolute", right: 12, top: 12 }}
              aria-label="Cerrar listado de voluntarios"
            >
              <CloseOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        {!canView ? (
          <Alert severity="warning">No tiene permiso para consultar los voluntarios de este despliegue.</Alert>
        ) : !idIsValid ? (
          <Alert severity="error">No se encontró la convocatoria seleccionada.</Alert>
        ) : (
          <Stack spacing={2.5}>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    {headerData.descripcionConvo ||
                      headerData.descripcion ||
                      `${MEDICAL_DEPLOYMENT_LABELS.singularShort} sin descripción`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[headerData.nombreProyecto, headerData.codigoProyecto].filter(Boolean).join(" · ") || "Sin proyecto"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[formatDate(headerData.fecha), headerData.comunidad].filter((value) => value && value !== "-").join(" · ") || "Sin ubicación"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actividad: {formatActivity(headerData.actividad)}
                  </Typography>
                </Box>
                <Chip
                  label={estadoOperativoVisual.label}
                  color={estadoOperativoVisual.chipColor}
                  variant="filled"
                  sx={{ fontWeight: 900, minWidth: 92 }}
                />
              </Box>
            </Box>

            <Box component="section" aria-label="Filtros del listado de voluntarios">
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar por nombre, apellido, correo o teléfono"
                    value={filters.q}
                    onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Estado de participación"
                    value={filters.estado}
                    onChange={(event) => setFilters((current) => ({ ...current, estado: event.target.value }))}
                  >
                    {estadoOptions.map((option) => (
                      <MenuItem key={option.value || "todos"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Asistencia"
                    value={filters.asistencia}
                    onChange={(event) => setFilters((current) => ({ ...current, asistencia: event.target.value }))}
                  >
                    {asistenciaOptions.map((option) => (
                      <MenuItem key={option.value || "todas"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<FilterAltOutlinedIcon />}
                      onClick={handleApplyFilters}
                      disabled={loading}
                    >
                      Aplicar filtros
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<RestartAltOutlinedIcon />}
                      onClick={handleClearFilters}
                      disabled={loading}
                    >
                      Limpiar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Box display="flex" alignItems="center" gap={1.5} mb={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {hasAppliedFilters ? "Resumen del resultado filtrado" : "Resumen de la convocatoria"}
                </Typography>
                {loading && <CircularProgress size={20} />}
              </Box>
              <Grid container spacing={1}>
                {summaryItems.map(([label, value]) => (
                  <Grid item xs={6} sm={4} md={3} lg={1.5} key={label}>
                    <SummaryItem label={label} value={value} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {loading && items.length === 0 ? (
              <Box minHeight={220} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : items.length === 0 && !error ? (
              <Alert severity="info">
                {hasAppliedFilters
                  ? "No hay voluntarios para los filtros seleccionados."
                  : "Este despliegue todavía no tiene participantes registrados."}
              </Alert>
            ) : (
              <Box>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Mostrando {fromRow}–{toRow} de {items.length} voluntarios
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 480, overflow: "auto" }}>
                  <Table stickyHeader size="small" aria-label="Voluntarios del despliegue" sx={{ minWidth: 1450 }}>
                    <TableHead>
                      <TableRow>
                        {[
                          "Voluntario",
                          "Correo",
                          "Teléfono",
                          "Profesión",
                          "Rol",
                          "Estado",
                          "Asistencia",
                          "Refrigerio",
                          "Tipo de asignación",
                          "Motivo de falta",
                          "Fecha de registro",
                        ].map((column) => (
                          <TableCell key={column} sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>
                            {column}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedItems.map((item) => {
                        const volunteerName =
                          item.nombreCompleto ||
                          [item.nombres, item.apellidos].filter(Boolean).join(" ") ||
                          item.email ||
                          "-";
                        const estadoLabel = item.estadoParticipacionLabel || "Sin clasificar";
                        const estadoVisual = getEstadoParticipacionVisual(estadoLabel);
                        const asistenciaLabel =
                          item.asistenciaLabel || asistenciaLabels[String(item.asistencia)] || "Sin procesar";
                        const refrigerioLabel =
                          item.refrigerioLabel || (Number(item.refrigerio) === 1 ? "Con refrigerio" : "No solicitado");

                        return (
                          <TableRow
                            key={item.idParticipacion || `${item.email}-${item.fechaCreacion}`}
                            hover
                            sx={{
                              borderLeft: `4px solid ${estadoVisual.border}`,
                              "&:hover": { backgroundColor: "action.hover" },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{volunteerName}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{item.email || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{item.telefono || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{item.profesion || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{item.rol || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Chip
                                label={estadoLabel}
                                color={estadoVisual.chipColor}
                                size="small"
                                variant="filled"
                                sx={{ fontWeight: 800 }}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Chip
                                label={asistenciaLabel}
                                color={getAsistenciaColor(asistenciaLabel)}
                                size="small"
                                variant="filled"
                                sx={{ fontWeight: 800 }}
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{refrigerioLabel}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {item.tipoAsignacionLabel || item.tipoAsignacion || "-"}
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>{item.motivoFalta || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(item.fechaCreacion)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={items.length}
                  page={page}
                  onPageChange={(event, nextPage) => setPage(nextPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VoluntariosConvocatoriaOperativaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  convocatoria: PropTypes.object,
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default VoluntariosConvocatoriaOperativaDialog;
